import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { excelExpertReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

const image1 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample2.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/ExcelLogo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function ExcelExpert() {
  const safeReviews = Array.isArray(excelExpertReviews) ? excelExpertReviews : [];
  const title = "Excel Expert — Formulas, Dashboards, VBA Automation & Spreadsheet Fixes";
  const description =
    "Need an Excel expert? Get help with formulas, dashboards, VBA automation, reporting, cleanup, and spreadsheet troubleshooting.";
  const canonical = "https://brainwavesolutions.net/services/excel-expert";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Excel Expert", url: canonical },
    ]),
    buildServiceSchema({
      name: "Excel Expert",
      description,
      canonical,
      offerLabel: "$50+",
      serviceType: "Excel support and automation",
    }),
  ];

  const images = [
    {
      image: image1,
      altText:
        "Excel dashboard template for job tracking with journal entry tables, project details fields, task lists, and a built-in timer control panel for daily operations reporting.",
    },
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Excel expert help — make your workbook accurate, faster, and easier to trust
              </Typography>

              <p className="bws-gsl-lead">
                Excel is incredibly useful — until the workbook becomes the thing slowing everyone down. Formulas get
                harder to follow, tabs start multiplying, reports take too long to update, and nobody wants to be the
                one who touches the wrong cell and breaks everything.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals clean that up. Whether you need a quick formula fix, a cleaner
                reporting workbook, a dashboard, or VBA automation, the goal is the same: make Excel work for you
                instead of creating more friction.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Excel formulas</span>
                <span className="bws-gsl-badge">Dashboards &amp; reporting</span>
                <span className="bws-gsl-badge">VBA automation</span>
                <span className="bws-gsl-badge">Workbook cleanup</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with Excel
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common Excel problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Broken formulas, mismatched totals, and unstable references</li>
                  <li>XLOOKUP, INDEX/MATCH, SUMIFS, pivots, validation, and reporting issues</li>
                  <li>Workbooks that are slow, messy, or difficult to maintain</li>
                  <li>Manual recurring work that should be automated with VBA</li>
                  <li>Dashboards and summaries that need to be clearer and more useful</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with a description of your project, and how I can be of assistance. I will reply ASAP, typically within 24 hours.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Fix what’s broken</Typography>
              <Typography className="bws-gsl-stripText">
                Wrong totals, broken formulas, unstable references, and workbooks nobody trusts.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make it cleaner</Typography>
              <Typography className="bws-gsl-stripText">
                Better layout, clearer inputs, simpler summaries, and a workbook that’s easier to maintain.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Automate the repeat work</Typography>
              <Typography className="bws-gsl-stripText">
                VBA tools and reporting workflows that reduce manual steps and save time.
              </Typography>
            </Paper>
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-sectionHead">
              <Typography className="bws-gsl-h2">Examples:</Typography>
              <p className="bws-gsl-p">
                Dashboard, workflow, and reporting examples built in Excel.
              </p>
            </div>

            <ImageCarousel images={images} />
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Excel formulas and logic troubleshooting</li>
                    <li>Dashboards, trackers, and reporting workbooks</li>
                    <li>Workbook cleanup and structural improvements</li>
                    <li>VBA automation for recurring processes</li>
                    <li>Operational spreadsheets that need to be reliable</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses using Excel as a core workflow tool</li>
                    <li>Teams sharing large or messy workbooks</li>
                    <li>People stuck on a workbook issue they can’t solve</li>
                    <li>Reporting, operations, budgeting, quoting, and scheduling</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="bws-gsl-section">
            <div className="bws-gsl-sectionHead">
              <Typography className="bws-gsl-h2">Client feedback</Typography>
            </div>

            <div className="bws-gsl-reviews">
              {safeReviews.slice(0, 3).map((r, idx) => (
                <Paper key={idx} className="bws-gsl-review" elevation={0}>
                  <Rating
                    value={5}
                    readOnly
                    size="small"
                    className="bws-gsl-reviewStars"
                    aria-label="5 out of 5 stars"
                  />
                  <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                  <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                </Paper>
              ))}
            </div>
          </div>

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you fix just one Excel formula?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Some requests are quick formula fixes, while others grow into workbook cleanup, dashboards, or VBA automation.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you automate Excel with VBA?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. I can build or improve VBA-based workflows for recurring tasks, reporting, and workbook actions.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the workbook problem and the end result you want. A screenshot or sample workbook helps a lot.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help with Excel?</Typography>
              <p className="bws-muted">
                Send a quick description of what’s not working and what you want the workbook to do.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
